import React, { useCallback, useEffect, useRef, useState } from "react";
import { preloadDocumentPdf, useDocumentPdf } from "../../../fetch/fetchPdf";
import { pdfjs, Document, Page } from 'react-pdf';
import { useIsInViewport, useWindowDimensions } from "../../../hooks";
import DocumentScrollPagePane from "./DocumentScrollPagePane";
import ZoomablePageFab from "../../documentViewCore/ZoomablePageFab";
import { highlightPattern } from "../../documentViewCore/highlightHelpers";

export default function DocumentImagePane({ partNumber, pageNumber, hasCover, pageCount, documentDimensions, onComeIntoVisibility, setPageNumber, onToggleZoom, page, executedQuery }) {
    const containerRef = useRef(null);
    const [isInViewport, intersectionRatio] = useIsInViewport(containerRef);
    const { height: viewportHeight } = useWindowDimensions();

    const textRenderer = useCallback(
        (textItem) => highlightPattern(textItem.str, executedQuery),
        [executedQuery]
    );


    const pageChangeHeightThreshold = (viewportHeight - 139) / 2;

    useEffect(() => {
        containerRef.current.id = `page=${pageNumber}`
    }, [containerRef?.current]);

    const pdfPageNumber = hasCover ? pageNumber + 1 : pageNumber

    const [docRendered, setDocRendered] = useState(false);
    const { pdfData, isLoading: pdfIsLoading } = useDocumentPdf(isInViewport ? partNumber : null, pdfPageNumber);

    useEffect(() => {
        if (isInViewport) {
            if (intersectionRatio * documentDimensions.documentHeight >= pageChangeHeightThreshold) {
                onComeIntoVisibility(pageNumber);
            }

            if (isInViewport && pageNumber - 1 > 0) {
                preloadDocumentPdf(partNumber, pdfPageNumber - 1)
            }

            if (isInViewport && pageNumber < pageCount) {
                preloadDocumentPdf(partNumber, pdfPageNumber + 1)
            }
        }
    }, [isInViewport, intersectionRatio, pageChangeHeightThreshold, documentDimensions.documentHeight, pageNumber, partNumber, pageCount]);

    const containerRatio = Math.round((documentDimensions.documentHeight / documentDimensions.documentWidth) * 10) / 10;
    const { pageHeight, pageWidth } = page;
    const pageRatio = Math.round((pageHeight / pageWidth) * 10) / 10;

    return (
        <DocumentScrollPagePane
            partNumber={partNumber}
            pageNumber={pageNumber}
            pageCount={pageCount}
            documentDimensions={documentDimensions}
            isInViewport={isInViewport}
            pdfRendered={docRendered}
            ref={containerRef}
        >
            {containerRatio != pageRatio ? (
                <ZoomablePageFab
                    partNumber={partNumber}
                    pageNumber={pageNumber}
                    position='right'
                />
            ) : null}
            {!pdfIsLoading && !!pdfData && isInViewport ? (
                <div style={{ display: !docRendered ? 'none' : null, height: '100%', width: '100%' }} onDoubleClick={onToggleZoom}>
                    <Document file={pdfData} externalLinkTarget="_blank" loading='' onLoadSuccess={(args) => { setTimeout(() => setDocRendered(true), 500); }} onItemClick={({ dest, pageIndex, pageNumber }) => { setPageNumber(pageNumber) }}>
                        <Page
                            loading=''
                            noData=''
                            pageNumber={1}
                            renderTextLayer={true}
                            height={documentDimensions.documentHeight}
                            width={documentDimensions.documentWidth}
                            renderAnnotationLayer={true}
                            customTextRenderer={textRenderer}
                        />
                    </Document>
                </div>
            ) : null}
        </DocumentScrollPagePane>
    );
}